import FetchHelper from '../helpers/fetch_helper';
import Log from '../helpers/log';

const RequestDispatcher = {
  async fetchResources(data) {
    return this.dispatch({ type: 'makeRequest', data: { ...data, method: 'GET' } });
  },

  deleteResource(data) {
    return this.dispatch({ type: 'makeRequest', data: { ...data, method: 'DELETE' } });
  },

  createResource(data) {
    return this.dispatch({ type: 'makeRequest', data: { ...data, method: 'POST' } });
  },

  async updateResource(data) {
    return this.dispatch({ type: 'makeRequest', data: { ...data, method: 'PUT' } });
  },

  async makeRequest({ version, url, body, method, setAction, setActionData, storeKey, errorMessage, successMessage }) {
    try {
      let options = { method, version };
      if (body) options.body = body;
      const response = await FetchHelper.fetchJson(url, options);
      if (setActionData) response.results.setActionData = setActionData;

      if (setAction) await this.dispatch({ type: setAction, data: response.results });
      if (storeKey) this.setStoreVal(storeKey, response.results);
      if (successMessage) this.dispatch({ type: 'setMessages', data: [successMessage] });
      return response?.results?.[0];
    } catch (error) {
      if (error.response.status !== 400) {
        this.dispatch({
          type: 'handleRequestError',
          data: {
            error,
            defaultErrorMessage: 'errorMessage',
          },
        });
      }
      throw error;
    }
  },

  fetchLegalDocuments(proposalId) {
    this.dispatch({
      type: 'fetchResources',
      data: {
        url: `proposals/${proposalId}/legal-documents`,
        storeKey: 'legalDocuments',
        errorMessage: 'Failed to fetch legal documents',
      },
    });
  },
  async clearCustomersList() {
    this.setStoreVal('customers', []);
  },

  getSearchParams() {
    return this.getStoreVal('additionalParams');
  },

  async fetchViewableOwners() {
    try {
      const viewableOwners = await FetchHelper.fetchJson('users/viewable-owners', { method: 'get' });
      this.setStoreVal('viewableOwners', viewableOwners.results);
    } catch (error) {
      this.dispatch({ type: 'handleRequestError', data: { error, defaultErrorMessage: 'Failed to fetch owners' } });
    }
  },

  async fetchCustomersList({ additionalParams, pageNumber, refresh }) {
    if (typeof additionalParams !== 'undefined' && additionalParams.length !== 0) {
      //do nothing
    } else if (typeof this.getStoreVal('additionalParams') !== 'undefined') {
      additionalParams = this.getStoreVal('additionalParams');
    } else {
      additionalParams = [];
    }

    const additionalParamsAsString = additionalParams.reduce((resultString, keyValueObject) => {
      return '&' + keyValueObject.key + '=' + keyValueObject.value;
    }, '');

    if (typeof pageNumber === 'undefined') {
      pageNumber = 1;
    }

    const url = 'customers?pageNumber=' + pageNumber + '&pageSize=50&includeStatuses=false' + additionalParamsAsString;
    if (url !== this.getStoreVal('requestUrl') || refresh) {
      this.setStoreVal('requestUrl', url);

      if (this.acquireLock('fetchCustomersList')) {
        try {
          const response = await FetchHelper.fetchJson(url, { method: 'GET' });
          if (response.count > 0) {
            console.log(response);
            this.setStoreVal('additionalParams', additionalParams);
            this.setStoreVal('pageNumber', pageNumber);
            this.concatStoreVal('customers', response.results);
          } else if (response.messages[0] === 'No customers found') {
            if (pageNumber === 1) {
              this.setStoreVal('customers', ['none found']);
            }
          }
        } catch (error) {
          this.dispatch({
            type: 'handleRequestError',
            data: {
              error,
              defaultErrorMessage: 'Cannot fetch customers',
            },
          });
        }
        this.releaseLock('fetchCustomersList');
      }
    }
  },

  async fetchPaymentPageConfig() {
    let paymentPageConfigResponse;
    let failureMessage;
    let requestError;

    try {
      paymentPageConfigResponse = await FetchHelper.fetchJson('payment-page-config', { method: 'GET' });
    } catch (error) {
      requestError = error;
      failureMessage = 'Failed to fetch payment page config';
    }

    let paymentPageConfig;
    if (!failureMessage) {
      if (paymentPageConfigResponse.results && paymentPageConfigResponse.results.length > 0) {
        paymentPageConfig = paymentPageConfigResponse.results[0];
        if (paymentPageConfig && paymentPageConfig.hostedPages && paymentPageConfig.hostedPages.length > 0) {
          paymentPageConfig.hostedPages.forEach((hostedPage) => {
            if (!hostedPage.rsaSignature) {
              failureMessage = "Payment config didn't have all RSA Signatures";
            }
          });
        } else {
          failureMessage = "Payment config didn't have any hosted pages";
        }
      } else {
        failureMessage = "Payment config didn't contain any results";
      }
    }

    if (!failureMessage) {
      this.setStoreVal('paymentPageConfig', paymentPageConfig);
    } else {
      let errorJson = null;
      if (requestError && requestError.response) {
        errorJson = await requestError.response.text();
      }

      Log.error(failureMessage, JSON.parse(errorJson));

      this.dispatch({ type: 'setRoute', data: '/confirmation' });
    }
  },

  createCustomer({ body }) {
    return this.dispatch({
      type: 'createResource',
      data: {
        url: 'customers',
        body,
        setAction: 'setCustomerResults',
        errorMessage: 'Failed to create customer',
        successMessage: 'Customer created',
      },
    });
  },

  async updateCustomer({ skipValidation = false, customerId, body, version = '/v1' }) {
    let success = true;
    this.incrementLock('updateCustomer');
    try {
      await this.dispatch({
        type: 'updateResource',
        data: {
          version,
          url: `customers/${customerId}`,
          body,
          setAction: 'setCustomerResults',
          errorMessage: 'Failed to update customer',
        },
      });
    } catch (error) {
      if (!skipValidation) {
        switch (error.response.status) {
          case 400:
            const errorBody = await error.response.clone().json();

            if (errorBody?.fieldErrors?.some((o) => o.code === 'DUPLICATE_CUSTOMER')) {
              this.dispatch({ type: 'showDuplicateEmailModal', data: { show: true, error: errorBody.messages[0] } });
            }
            break;

          default:
            error.response
              .clone()
              .json()
              .then((response) => {
                this.dispatch({ type: 'setErrorMessages', data: response.messages[0] });
              });
            break;
        }
      }
      success = false;
    }
    this.decrementLock('updateCustomer');
    return success;
  },

  async updateListViewCustomer({ customerId, body }) {
    let success = true;
    this.incrementLock('updateCustomer');
    try {
      await this.dispatch({
        type: 'updateResource',
        data: {
          url: `customers/${customerId}`,
          body,
          errorMessage: 'Failed to update customer',
        },
      });
    } catch (error) {
      success = false;
    }
    this.decrementLock('updateCustomer');
    return success;
  },

  updateEnergyDetails({ customerId, body }) {
    return this.dispatch({
      type: 'updateResource',
      data: {
        url: `customers/${customerId}/energy-details`,
        body,
        errorMessage: 'Failed to update energy details',
        storeKey: 'energyDetails',
      },
    });
  },

  async fetchSystemArrays(proposalId) {
    await this.dispatch({
      type: 'fetchResources',
      data: {
        url: `proposals/${proposalId}/quoting-pv-system`,
        storeKey: 'systemArrays',
        errorMessage: 'Cannot fetch System Arrays',
      },
    });

    const systemArrays = this.getStoreVal('systemArrays');
    const editedInTools = systemArrays
      .filter(({ x, y }) => x === null && y === null)
      .some(() => {
        return true;
      });
    this.setStoreVal('editedInTools', editedInTools);
  },

  acquireLock(key) {
    return this.acquireLock(key);
  },

  releaseLock(key) {
    return this.decrementLock(key);
  },
};
export default RequestDispatcher;
